// ABOUTME: Normalizes the company rollout mode for WhatsApp contact episodes.
// ABOUTME: Makes missing and invalid backend values fail closed across frontend consumers.
export type WhatsappContactEpisodeMode =
    "OFF" | "SHADOW" | "READ_ONLY" | "ACTIVE";

export interface WhatsappContactEpisodeConsent {
    optedOut: boolean;
    marketingOptedOut: boolean;
    phoneSuppressed: boolean;
    manualOptInBlocked: boolean;
    allowedCategories: string[];
    blockedCategories: string[];
}

export interface WhatsappContactEpisodeActionContext {
    contactEpisodeId: string;
    resolutionRevision: number;
    stateRevision?: number;
    maskedPhone?: string;
    lastActivityAt?: string;
    hasActiveCustomerServiceWindow?: boolean;
    canSendSessionMessage?: boolean;
    canSendTemplate?: boolean;
    optedIn?: boolean;
    consent?: WhatsappContactEpisodeConsent;
}

export interface WhatsappContactEpisodeActionEligibility {
    optedIn: boolean;
    optedOut: boolean;
    marketingOptedOut: boolean;
    phoneSuppressed: boolean;
    manualOptInBlocked: boolean;
    hasActiveCustomerServiceWindow: boolean;
    canSendSessionMessage: boolean;
    canSendTemplate: boolean;
}

const whatsappContactEpisodeModes = new Set<WhatsappContactEpisodeMode>([
    "OFF",
    "SHADOW",
    "READ_ONLY",
    "ACTIVE"
]);

export const normalizeWhatsappContactEpisodeMode = (
    value?: any
): WhatsappContactEpisodeMode => {
    const mode = String(value || "").trim() as WhatsappContactEpisodeMode;
    return whatsappContactEpisodeModes.has(mode) ? mode : "OFF";
};

export const shouldShowWhatsappContactEpisodeInbox = (value?: any) => {
    const mode = normalizeWhatsappContactEpisodeMode(value);
    return mode === "READ_ONLY" || mode === "ACTIVE";
};

export const maskWhatsappContactEpisodePhone = (value?: any) => {
    const phone = String(value || "").trim();
    if (!phone || phone.includes("*")) {
        return phone;
    }

    const digits = phone.replace(/\D/g, "");
    if (digits.length <= 4) {
        return "*".repeat(digits.length);
    }

    let visibleDigits = 4;
    return phone
        .split("")
        .reverse()
        .map((character) => {
            if (!/\d/.test(character)) {
                return character;
            }
            if (visibleDigits > 0) {
                visibleDigits -= 1;
                return character;
            }
            return "*";
        })
        .reverse()
        .join("");
};

export const getWhatsappContactEpisodeActionContext = (
    value?: Compleo.IObject | null
): WhatsappContactEpisodeActionContext | undefined => {
    const contactEpisodeId = String(
        value?.contactEpisodeId ||
            value?.conversationReference?.contactEpisodeId ||
            ""
    ).trim();
    const resolutionRevision = Number(value?.resolutionRevision);
    if (
        !contactEpisodeId ||
        !Number.isSafeInteger(resolutionRevision) ||
        resolutionRevision < 0
    ) {
        return undefined;
    }

    const context: WhatsappContactEpisodeActionContext = {
        contactEpisodeId,
        resolutionRevision
    };
    const stateRevision = Number(value?.stateRevision);
    if (Number.isSafeInteger(stateRevision) && stateRevision >= 0) {
        context.stateRevision = stateRevision;
    }
    if (value?.maskedPhone) {
        context.maskedPhone = maskWhatsappContactEpisodePhone(
            value.maskedPhone
        );
    }
    if (value?.lastActivityAt) {
        context.lastActivityAt = String(value.lastActivityAt);
    }
    [
        "hasActiveCustomerServiceWindow",
        "canSendSessionMessage",
        "canSendTemplate",
        "optedIn"
    ].forEach((field) => {
        if (typeof value?.[field] === "boolean") {
            (context as Compleo.IObject)[field] = value[field];
        }
    });
    if (value?.consent && typeof value.consent === "object") {
        context.consent = {
            optedOut: value.consent.optedOut === true,
            marketingOptedOut: value.consent.marketingOptedOut === true,
            phoneSuppressed: value.consent.phoneSuppressed === true,
            manualOptInBlocked: value.consent.manualOptInBlocked === true,
            allowedCategories: Array.isArray(value.consent.allowedCategories)
                ? value.consent.allowedCategories.map(String)
                : [],
            blockedCategories: Array.isArray(value.consent.blockedCategories)
                ? value.consent.blockedCategories.map(String)
                : []
        };
    }

    return context;
};

export const getWhatsappContactEpisodeActionEligibility = (
    fallback: WhatsappContactEpisodeActionEligibility,
    context?: WhatsappContactEpisodeActionContext
): WhatsappContactEpisodeActionEligibility => {
    const normalizedContext = getWhatsappContactEpisodeActionContext(context);
    if (!normalizedContext) {
        return fallback;
    }

    return {
        optedIn: normalizedContext.optedIn === true,
        optedOut: normalizedContext.consent?.optedOut === true,
        marketingOptedOut:
            normalizedContext.consent?.marketingOptedOut === true,
        phoneSuppressed: normalizedContext.consent?.phoneSuppressed === true,
        manualOptInBlocked:
            normalizedContext.consent?.manualOptInBlocked === true,
        hasActiveCustomerServiceWindow:
            normalizedContext.hasActiveCustomerServiceWindow === true,
        canSendSessionMessage: normalizedContext.canSendSessionMessage === true,
        canSendTemplate: normalizedContext.canSendTemplate === true
    };
};

export const getWhatsappContactEpisodeActionContexts = (
    values: Compleo.IObject[] = []
) => {
    const contexts = new Map<string, WhatsappContactEpisodeActionContext>();
    const conflictingEpisodeIds = new Set<string>();

    values.forEach((value) => {
        const context = getWhatsappContactEpisodeActionContext(value);
        if (!context) {
            return;
        }

        const current = contexts.get(context.contactEpisodeId);
        if (
            current &&
            current.resolutionRevision !== context.resolutionRevision
        ) {
            contexts.delete(context.contactEpisodeId);
            conflictingEpisodeIds.add(context.contactEpisodeId);
            return;
        }
        if (!conflictingEpisodeIds.has(context.contactEpisodeId)) {
            contexts.set(context.contactEpisodeId, context);
        }
    });

    return Array.from(contexts.values());
};

export const getWhatsappContactEpisodeActionContextForMode = (
    mode: WhatsappContactEpisodeMode,
    context?: WhatsappContactEpisodeActionContext
) =>
    normalizeWhatsappContactEpisodeMode(mode) === "ACTIVE"
        ? getWhatsappContactEpisodeActionContext(context)
        : undefined;

const declaresWhatsappContactEpisodeIdentity = (
    value?: Compleo.IObject | null
) =>
    String(value?.scope || "") === "CONTACT_EPISODE" ||
    String(value?.conversationReference?.scope || "") === "CONTACT_EPISODE" ||
    Boolean(
        String(
            value?.contactEpisodeId ||
                value?.conversationReference?.contactEpisodeId ||
                ""
        ).trim()
    );

export const isWhatsappReplyTargetInActionContext = (
    message: Compleo.IObject,
    context?: WhatsappContactEpisodeActionContext
) => {
    const messageContext = getWhatsappContactEpisodeActionContext(message);
    const actionContext = getWhatsappContactEpisodeActionContext(context);
    if (
        (declaresWhatsappContactEpisodeIdentity(message) && !messageContext) ||
        (declaresWhatsappContactEpisodeIdentity(context) && !actionContext)
    ) {
        return false;
    }
    if (!messageContext || !actionContext) {
        return !messageContext && !actionContext;
    }
    return (
        messageContext.contactEpisodeId === actionContext.contactEpisodeId &&
        messageContext.resolutionRevision === actionContext.resolutionRevision
    );
};

export const resolveWhatsappReplyToMessageId = (
    message: Compleo.IObject | null,
    context?: WhatsappContactEpisodeActionContext
) => {
    if (!message) {
        return "";
    }
    if (!isWhatsappReplyTargetInActionContext(message, context)) {
        return undefined;
    }
    const messageId = String(message.messageId || "").trim();
    return messageId || undefined;
};

export const canSaveWhatsappContactEpisodeMedia = (
    mode: WhatsappContactEpisodeMode,
    message: Compleo.IObject
) => {
    if (!declaresWhatsappContactEpisodeIdentity(message)) {
        return true;
    }
    return (
        Boolean(getWhatsappContactEpisodeActionContext(message)) &&
        normalizeWhatsappContactEpisodeMode(mode) === "ACTIVE"
    );
};

export const resolveWhatsappContactEpisodeActionContextSelection = (
    contexts: WhatsappContactEpisodeActionContext[],
    selected?: WhatsappContactEpisodeActionContext
) => {
    const selectedIdentity = getWhatsappContactEpisodeActionContext(selected);
    if (!selectedIdentity) {
        return undefined;
    }

    return contexts.find(
        (context) =>
            context.contactEpisodeId === selectedIdentity.contactEpisodeId &&
            context.resolutionRevision === selectedIdentity.resolutionRevision
    );
};

export const buildWhatsappContactEpisodeActionRequest = <
    Request extends Compleo.IObject
>(
    request: Request,
    context?: WhatsappContactEpisodeActionContext,
    actionRequestId?: string
) => {
    const normalizedContext = getWhatsappContactEpisodeActionContext(context);
    if (!normalizedContext) {
        return request;
    }

    const episodeRequest: Compleo.IObject = { ...request };
    delete episodeRequest.applicantId;
    delete episodeRequest.segmentId;
    delete episodeRequest.phone;
    delete episodeRequest.remotePhoneDigits;

    return {
        ...episodeRequest,
        conversationReference: {
            scope: "CONTACT_EPISODE" as const,
            contactEpisodeId: normalizedContext.contactEpisodeId
        },
        contactEpisodeId: normalizedContext.contactEpisodeId,
        resolutionRevision: normalizedContext.resolutionRevision,
        actionRequestId: String(actionRequestId || "")
    };
};

let actionRequestSequence = 0;

export const createWhatsappActionRequestId = () => {
    if (
        typeof globalThis.crypto !== "undefined" &&
        typeof globalThis.crypto.randomUUID === "function"
    ) {
        return globalThis.crypto.randomUUID();
    }

    actionRequestSequence += 1;
    return `${Date.now().toString(36)}-${actionRequestSequence.toString(36)}`;
};

export const createWhatsappActionRequestTracker = (
    createRequestId: () => string = createWhatsappActionRequestId
) => {
    const attempts = new Map<
        string,
        { signature: string; actionRequestId: string }
    >();

    return {
        prepare: (action: string, signature: string) => {
            const normalizedAction = String(action || "");
            const normalizedSignature = String(signature || "");
            const current = attempts.get(normalizedAction);
            if (current?.signature === normalizedSignature) {
                return current;
            }
            const attempt = {
                signature: normalizedSignature,
                actionRequestId: createRequestId()
            };
            attempts.set(normalizedAction, attempt);
            return attempt;
        },
        complete: (action: string, signature: string) => {
            const normalizedAction = String(action || "");
            if (
                attempts.get(normalizedAction)?.signature === String(signature)
            ) {
                attempts.delete(normalizedAction);
            }
        },
        clear: (action: string) => {
            attempts.delete(String(action || ""));
        }
    };
};
